/**
 * StepIndicator Component
 * Displays progress through the signup wizard with step numbers and connecting lines.
 * Supports non-sequential step numbers (e.g., step 5 hidden when Stripe handles checkout).
 */

import React from 'react';
import { Check } from 'lucide-react';
import type { WizardStep } from '../hooks/useSignupWizard';

interface StepIndicatorProps {
  currentStep: WizardStep;
  steps: Array<{ number: WizardStep; label: string }>;
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep, steps }) => {
  return (
    <div className="w-full max-w-md mx-auto mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = currentStep > step.number;
          const isCurrent = currentStep === step.number;
          // For non-sequential steps (e.g., currentStep=5 between 4 and 6),
          // treat it as being on the previous visible step
          const isInBetween = !isCurrent && !isCompleted &&
            index > 0 && currentStep > steps[index - 1].number && currentStep < step.number;
          const isLast = index === steps.length - 1;
          const displayNumber = index + 1;

          return (
            <React.Fragment key={step.number}>
              {/* Step circle */}
              <div className="flex flex-col items-center">
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    font-semibold text-sm transition-all duration-300
                    ${isCompleted
                      ? 'bg-emerald-600 text-white'
                      : (isCurrent || isInBetween)
                        ? 'bg-emerald-600 text-white ring-4 ring-emerald-100'
                        : 'bg-slate-200 text-slate-500'
                    }
                  `}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    displayNumber
                  )}
                </div>
                <span
                  className={`
                    mt-2 text-xs font-medium hidden sm:block
                    ${(isCurrent || isInBetween) ? 'text-emerald-600' : 'text-slate-500'}
                  `}
                >
                  {step.label}
                </span>
              </div>

              {/* Connecting line */}
              {!isLast && (
                <div
                  className={`
                    flex-1 h-1 mx-2 rounded-full transition-all duration-300
                    ${isCompleted ? 'bg-emerald-600' : 'bg-slate-200'}
                  `}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Mobile step label */}
      <div className="sm:hidden mt-4 text-center">
        <span className="text-sm font-medium text-emerald-600">
          {steps.find(s => s.number === currentStep)?.label ||
           steps.find(s => s.number > currentStep)?.label ||
           steps[steps.length - 1]?.label}
        </span>
      </div>
    </div>
  );
};

export default StepIndicator;
